/**
 * Main application module - ties all components together
 */

import {
    MORSE_CODE,
    ALL_CHARACTERS,
    calculateDotDurationMs,
    calculateDashDurationMs,
    calculateSymbolSpacingMs,
    PATTERN_GROUPS
} from './morse.js';

import {
    initializeCharStats,
    updateCharacterStats,
    revertToIncorrect,
    generatePracticeQueue
} from './spaced-repetition.js';

import {
    audioManager,
    initAudio,
    stopSound,
    playMorseSequence
} from './audio.js';

import {
    initializeConfusionMatrix,
    updateConfusionMatrix,
    analyzeConfusion,
    trackAttempt,
    calculateRobustAccuracy,
    getPerformanceZone,
    calculateSessionStats,
    formatTime,
    getSpeedRecommendationRobust,
    calculateRecognitionAdjustments,
    getCharacterPriorityScores
} from './stats.js';

import * as UI from './ui.js';

import { readInt, readString, write, readJSON, writeJSON, remove } from './storage.js';

// Constants
const TIMER_UPDATE_INTERVAL_MS = 1000;
const DEBOUNCE_DELAY_MS = 50;
const RECENT_ATTEMPTS_COUNT = 50;

// Persisted training progress
const PROGRESS_STORAGE_KEY = 'morsemind-progress';
const PROGRESS_SCHEMA_VERSION = 1;
const PROGRESS_SAVE_DEBOUNCE_MS = 1000;

// Application state
let DOM = null;
let isPlaying = false;
let isStarting = false;
let currentChar = '';
let previousChar = '';
let currentPhase = 'idle'; // 'idle' | 'morse' | 'recognition' | 'reveal' | 'oops-window'
let oopsPressed = false;
let previousRecordedAsSuccess = false;
let previousCharSnapshot = null;
let oopsCooldown = false;
let markedCharacter = null;
let practiceQueue = [];
let currentStreak = 0;
let sessionBestStreak = 0;
let sessionMissCounts = {};
let wakeLock = null;

// Session state
let sessionStats = {
    correct: 0,
    incorrect: 0,
    startTime: null,
    elapsedTimeMs: 0
};

// Track recognition time changes
let attemptsSinceRecognitionChange = 0;

// Character statistics
let charStats = {};
let confusionMatrix = {};
let recentAttempts = [];

// Timers
let practiceTimer = null;
let elapsedTimeInterval = null;
let progressSaveTimer = null;
const pendingTimers = new Set();

/**
 * setTimeout wrapper that tracks the handle so stopPractice/cleanup
 * can cancel everything still pending
 */
function schedule(fn, delayMs) {
    const id = setTimeout(() => {
        pendingTimers.delete(id);
        fn();
    }, delayMs);
    pendingTimers.add(id);
    return id;
}

function cancelScheduled(id) {
    if (id !== null) {
        clearTimeout(id);
        pendingTimers.delete(id);
    }
}

function clearAllTimers() {
    pendingTimers.forEach(id => clearTimeout(id));
    pendingTimers.clear();
    practiceTimer = null;
}

// Audio buffer cache
const audioBufferCache = new Map();
const audioDataCache = new Map();

// Settings
let currentSpeedWpm = 32;
let currentToneFreqHz = 500;
let currentToneVolumePercent = 50;
let currentVoiceVolumePercent = 90;
let currentVoice = 'chris';

// Event listeners tracking
const eventListeners = new Map();

/**
 * Detect if device has touch capability
 * @returns {boolean} True if device has touch support
 */
function hasTouchCapability() {
    return 'ontouchstart' in window || 
           navigator.maxTouchPoints > 0 || 
           navigator.msMaxTouchPoints > 0;
}

/**
 * Initialize the application
 */
export async function initialize() {
    // Initialize DOM elements
    DOM = UI.initializeDOMElements();

    // Initialize stats, then layer persisted progress on top
    charStats = initializeCharStats(ALL_CHARACTERS);
    confusionMatrix = initializeConfusionMatrix(ALL_CHARACTERS);
    loadProgress();

    // Load saved settings
    loadSettings();

    // Set up event listeners
    setupEventListeners();

    // Initialize UI
    UI.updatePracticeState(DOM, false);
    UI.updateRecognitionDisplay(DOM, getRecognitionTimeMs());

    // Preload audio data
    preloadAudioData();

    // Update tips based on platform
    updatePlatformTips();

    // Handle PWA shortcut
    handlePWAShortcut();

    // Show loaded training history right away
    if (recentAttempts.length > 0) {
        updateCharStatsDisplay();
    }
}

/**
 * Load persisted training progress from localStorage
 */
function loadProgress() {
    const saved = readJSON(PROGRESS_STORAGE_KEY);
    if (!saved || saved.version !== PROGRESS_SCHEMA_VERSION) return;

    // Merge saved per-character stats onto the initialized set so any
    // characters added later keep their defaults
    if (saved.charStats) {
        Object.keys(charStats).forEach(char => {
            if (saved.charStats[char]) {
                charStats[char] = { ...charStats[char], ...saved.charStats[char] };
            }
        });
    }
    if (saved.confusionMatrix) {
        Object.keys(confusionMatrix).forEach(char => {
            if (saved.confusionMatrix[char]) {
                confusionMatrix[char] = saved.confusionMatrix[char];
            }
        });
    }
    if (Array.isArray(saved.recentAttempts)) {
        recentAttempts = saved.recentAttempts.slice(-RECENT_ATTEMPTS_COUNT);
    }
    if (typeof saved.currentStreak === 'number') {
        currentStreak = saved.currentStreak;
    }
}

/**
 * Save training progress to localStorage
 */
function saveProgress() {
    if (progressSaveTimer !== null) {
        clearTimeout(progressSaveTimer);
        progressSaveTimer = null;
    }
    writeJSON(PROGRESS_STORAGE_KEY, {
        version: PROGRESS_SCHEMA_VERSION,
        charStats,
        confusionMatrix,
        recentAttempts,
        currentStreak
    });
}

/**
 * Debounced save - one write per burst of attempts.
 * Uses its own timer (not the practice registry) so stopping a session
 * can flush rather than drop a pending save.
 */
function scheduleProgressSave() {
    if (progressSaveTimer !== null) return;
    progressSaveTimer = setTimeout(saveProgress, PROGRESS_SAVE_DEBOUNCE_MS);
}

/**
 * Set a slider's spoken value for assistive tech
 */
function setSliderValueText(slider, text) {
    if (slider) {
        slider.setAttribute('aria-valuetext', text);
    }
}

/**
 * Load saved settings from localStorage
 */
function loadSettings() {
    // Character speed
    currentSpeedWpm = readInt('characterSpeed', { min: 10, max: 50, fallback: 32 });
    if (DOM.characterSpeedSlider) {
        DOM.characterSpeedSlider.value = currentSpeedWpm;
    }
    setSliderValueText(DOM.characterSpeedSlider, `${currentSpeedWpm} words per minute`);
    UI.updateSpeedDisplay(DOM, currentSpeedWpm);

    // Tone frequency
    currentToneFreqHz = readInt('toneFrequency', { min: 400, max: 800, fallback: 500 });
    if (DOM.toneFrequencySlider) {
        DOM.toneFrequencySlider.value = currentToneFreqHz;
    }
    setSliderValueText(DOM.toneFrequencySlider, `${currentToneFreqHz} hertz`);
    if (DOM.toneDisplay) {
        DOM.toneDisplay.textContent = currentToneFreqHz + ' Hz';
    }

    // Recognition time
    const recognitionTimeMs = readInt('recognitionTime', { min: 0, max: 3000, fallback: 500 });
    if (DOM.pauseDurationSlider) {
        DOM.pauseDurationSlider.value = recognitionTimeMs;
    }
    setSliderValueText(DOM.pauseDurationSlider, `${recognitionTimeMs} milliseconds`);
    if (DOM.pauseDisplay) {
        DOM.pauseDisplay.textContent = recognitionTimeMs + ' ms';
    }

    // Review time
    const reviewTimeMs = readInt('reviewTime', { min: 0, max: 3000, fallback: 1000 });
    if (DOM.reviewDurationSlider) {
        DOM.reviewDurationSlider.value = reviewTimeMs;
    }
    setSliderValueText(DOM.reviewDurationSlider, `${reviewTimeMs} milliseconds`);
    if (DOM.reviewDisplay) {
        DOM.reviewDisplay.textContent = reviewTimeMs + ' ms';
    }

    // Tone volume
    currentToneVolumePercent = readInt('toneVolume', { min: 0, max: 100, fallback: 50 });
    if (DOM.toneVolumeSlider) {
        DOM.toneVolumeSlider.value = currentToneVolumePercent;
    }
    setSliderValueText(DOM.toneVolumeSlider, `${currentToneVolumePercent} percent`);
    if (DOM.toneVolumeDisplay) {
        DOM.toneVolumeDisplay.textContent = currentToneVolumePercent + '%';
    }
    audioManager.setToneVolume(currentToneVolumePercent);

    // Voice volume
    currentVoiceVolumePercent = readInt('voiceVolume', { min: 0, max: 100, fallback: 90 });
    if (DOM.voiceVolumeSlider) {
        DOM.voiceVolumeSlider.value = currentVoiceVolumePercent;
    }
    setSliderValueText(DOM.voiceVolumeSlider, `${currentVoiceVolumePercent} percent`);
    if (DOM.voiceVolumeDisplay) {
        DOM.voiceVolumeDisplay.textContent = currentVoiceVolumePercent + '%';
    }

    // Voice selection
    currentVoice = readString('voice', ['chris', 'louisa'], 'chris');
    if (DOM.voiceSelect) {
        DOM.voiceSelect.value = currentVoice;
    }
    if (DOM.voiceDisplay) {
        DOM.voiceDisplay.textContent = currentVoice.charAt(0).toUpperCase() + currentVoice.slice(1);
    }
}

/**
 * Set up event listeners
 */
function setupEventListeners() {
    // Practice controls
    addTrackedEventListener(DOM.startButton, 'click', startPractice);
    addTrackedEventListener(DOM.stopButton, 'click', stopPractice);
    addTrackedEventListener(DOM.oopsButton, 'click', handleOops);

    // Settings
    addTrackedEventListener(DOM.settingsBtn, 'click', () => UI.showSettings(DOM));
    addTrackedEventListener(DOM.closeSettings, 'click', () => UI.hideSettings(DOM));
    addTrackedEventListener(DOM.resetProgressBtn, 'click', handleResetProgress);
    addTrackedEventListener(DOM.dismissSummary, 'click', () => UI.hideSessionSummary(DOM));
    
    // Close settings when clicking overlay
    const overlay = document.getElementById('overlay');
    if (overlay) {
        addTrackedEventListener(overlay, 'click', () => UI.hideSettings(DOM));
    }

    // Sliders
    addTrackedEventListener(DOM.characterSpeedSlider, 'input', debounce(handleSpeedChange, DEBOUNCE_DELAY_MS));
    addTrackedEventListener(DOM.toneFrequencySlider, 'input', debounce(handleToneChange, DEBOUNCE_DELAY_MS));
    addTrackedEventListener(DOM.pauseDurationSlider, 'input', debounce(handlePauseChange, DEBOUNCE_DELAY_MS));
    addTrackedEventListener(DOM.reviewDurationSlider, 'input', debounce(handleReviewChange, DEBOUNCE_DELAY_MS));
    addTrackedEventListener(DOM.toneVolumeSlider, 'input', debounce(handleToneVolumeChange, DEBOUNCE_DELAY_MS));
    addTrackedEventListener(DOM.voiceVolumeSlider, 'input', debounce(handleVoiceVolumeChange, DEBOUNCE_DELAY_MS));
    addTrackedEventListener(DOM.voiceSelect, 'change', handleVoiceChange);

    // Keyboard shortcuts
    addTrackedEventListener(document, 'keydown', handleKeyboard);

    // Recognition time adjustment button (event delegation)
    addTrackedEventListener(DOM.gaugeContainer, 'click', (e) => {
        const button = e.target.closest('.recognition-recommendation-btn');
        if (button && !button.disabled && button.dataset.adjustment) {
            const adjustment = parseInt(button.dataset.adjustment);
            const currentTimeMs = getRecognitionTimeMs();
            const newTimeMs = Math.max(0, Math.min(3000, currentTimeMs + adjustment));
            
            attemptsSinceRecognitionChange = 0;

            // Update slider and storage
            if (DOM.pauseDurationSlider) {
                DOM.pauseDurationSlider.value = newTimeMs;
                write('recognitionTime', newTimeMs);
                setSliderValueText(DOM.pauseDurationSlider, `${newTimeMs} milliseconds`);
                UI.updateRecognitionDisplay(DOM, newTimeMs);
            }
            
            // Update gauge immediately if playing
            if (isPlaying) {
                updateTrainingZoneGauge();
            }
        }
    });

    // Handle visibility change for wake lock
    addTrackedEventListener(document, 'visibilitychange', handleVisibilityChange);

    // Initialize audio on first interaction (pointerdown covers mouse, touch, and pen)
    addTrackedEventListener(document, 'pointerdown', ensureAudio, { once: true });

    // Cleanup on unload
    window.addEventListener('beforeunload', cleanup);
}

/**
 * Add event listener with tracking
 */
function addTrackedEventListener(element, event, handler, options) {
    if (!element) return;

    element.addEventListener(event, handler, options);

    const key = `${element.id || 'document'}_${event}`;
    if (!eventListeners.has(key)) {
        eventListeners.set(key, []);
    }
    eventListeners.get(key).push({ element, event, handler, options });
}

/**
 * Debounce function
 */
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Handle speed slider change
 */
function handleSpeedChange(event) {
    currentSpeedWpm = parseInt(event.target.value, 10);
    if (isNaN(currentSpeedWpm) || currentSpeedWpm < 10) currentSpeedWpm = 10;
    if (currentSpeedWpm > 50) currentSpeedWpm = 50;

    UI.updateSpeedDisplay(DOM, currentSpeedWpm);
    setSliderValueText(DOM.characterSpeedSlider, `${currentSpeedWpm} words per minute`);
    write('characterSpeed', currentSpeedWpm);
}

/**
 * Handle tone slider change
 */
function handleToneChange(event) {
    currentToneFreqHz = parseInt(event.target.value, 10);
    if (DOM.toneDisplay) DOM.toneDisplay.textContent = currentToneFreqHz + ' Hz';
    setSliderValueText(DOM.toneFrequencySlider, `${currentToneFreqHz} hertz`);
    write('toneFrequency', currentToneFreqHz);
}

/**
 * Handle pause slider change
 */
function handlePauseChange(event) {
    const value = parseInt(event.target.value, 10);

    attemptsSinceRecognitionChange = 0;
    write('recognitionTime', value);
    setSliderValueText(DOM.pauseDurationSlider, `${value} milliseconds`);
    UI.updateRecognitionDisplay(DOM, value);
}

/**
 * Handle review slider change
 */
function handleReviewChange(event) {
    const value = parseInt(event.target.value, 10);
    write('reviewTime', value);
    setSliderValueText(DOM.reviewDurationSlider, `${value} milliseconds`);
    if (DOM.reviewDisplay) {
        DOM.reviewDisplay.textContent = value + ' ms';
    }
}

/**
 * Handle tone volume slider change
 */
function handleToneVolumeChange(event) {
    currentToneVolumePercent = parseInt(event.target.value, 10);
    if (DOM.toneVolumeDisplay) DOM.toneVolumeDisplay.textContent = currentToneVolumePercent + '%';
    setSliderValueText(DOM.toneVolumeSlider, `${currentToneVolumePercent} percent`);
    write('toneVolume', currentToneVolumePercent);
    audioManager.setToneVolume(currentToneVolumePercent);
}

/**
 * Handle voice volume slider change
 */
function handleVoiceVolumeChange(event) {
    currentVoiceVolumePercent = parseInt(event.target.value, 10);
    if (DOM.voiceVolumeDisplay) DOM.voiceVolumeDisplay.textContent = currentVoiceVolumePercent + '%';
    setSliderValueText(DOM.voiceVolumeSlider, `${currentVoiceVolumePercent} percent`);
    write('voiceVolume', currentVoiceVolumePercent);
}

/**
 * Handle voice selection change
 */
async function handleVoiceChange(event) {
    const newVoice = event.target.value;
    if (newVoice === currentVoice) return;
    
    // Update current voice
    currentVoice = newVoice;

    // Clear existing cache and reload for the new voice
    audioBufferCache.clear();
    audioDataCache.clear();
    audioLoadPromise = null;

    await preloadAudioData();  // Fetch all files for new voice
    await ensureAudio();       // Decode them for playback

    // Update UI and save preference
    if (DOM.voiceDisplay) {
        DOM.voiceDisplay.textContent = currentVoice.charAt(0).toUpperCase() + currentVoice.slice(1);
    }
    write('voice', currentVoice);
}

/**
 * Handle the reset-progress button (destructive, so confirm first)
 */
function handleResetProgress() {
    if (isPlaying) return;
    if (!window.confirm('Reset all training progress? This cannot be undone.')) return;

    charStats = initializeCharStats(ALL_CHARACTERS);
    confusionMatrix = initializeConfusionMatrix(ALL_CHARACTERS);
    recentAttempts = [];
    currentStreak = 0;
    previousCharSnapshot = null;
    practiceQueue = [];
    remove(PROGRESS_STORAGE_KEY);

    updateCharStatsDisplay();
}

/**
 * Handle keyboard input
 */
function handleKeyboard(e) {
    // Don't hijack keys while focus is in the settings panel or a form control
    if (e.target.closest && (e.target.closest('#settings-panel') ||
        ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'].includes(e.target.tagName))) {
        if (e.key === 'Escape') {
            UI.hideSettings(DOM);
        }
        return;
    }

    if (e.key === 'Escape') {
        e.preventDefault();
        UI.hideSettings(DOM);
        if (isPlaying) {
            stopPractice();
        }
        return;
    }

    // S for start/stop
    if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        if (isPlaying) {
            stopPractice();
        } else {
            startPractice();
        }
        return;
    }

    // Spacebar for Oops
    if (e.key === ' ' && isPlaying) {
        e.preventDefault();
        handleOops(null);
    }
}

/**
 * Handle visibility change
 */
async function handleVisibilityChange() {
    if (document.hidden) {
        // Flush progress before the page is backgrounded (it may never come back)
        saveProgress();
        return;
    }
    if (isPlaying && !wakeLock) {
        await requestWakeLock();
    }
}

// Single-flight guard so concurrent triggers don't decode all buffers twice
let audioLoadPromise = null;

/**
 * Initialize audio and preload voice buffers (idempotent).
 * initialize() always re-runs because it resumes a still-suspended context;
 * the preload/decode work only ever runs once per voice.
 */
async function ensureAudio() {
    await initAudio();
    if (!audioLoadPromise) {
        audioLoadPromise = preloadAllAudio();
    }
    return audioLoadPromise;
}

/**
 * Preload audio data
 */
async function preloadAudioData() {
    const failedChars = [];
    const loadPromises = ALL_CHARACTERS.map(async (char) => {
        try {
            if (audioDataCache.has(char) || audioBufferCache.has(char)) return;

            const response = await fetch(`audio/${currentVoice}/${char}.mp3`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const arrayBuffer = await response.arrayBuffer();
            audioDataCache.set(char, arrayBuffer);
        } catch (err) {
            failedChars.push(char);
        }
    });

    await Promise.all(loadPromises);

    if (failedChars.length > 0) {
        console.error(`Failed to preload voice audio for ${failedChars.length} characters: ${failedChars.join(', ')}`);
    }
}

/**
 * Preload all audio files
 */
async function preloadAllAudio() {
    if (!audioManager.audioContext) {
        await initAudio();
    }

    // Decode pre-loaded audio data. Decoding detaches the source buffer,
    // so the raw entry is dropped either way; a failure re-fetches later.
    const decodePromises = [];
    for (const [char, arrayBuffer] of audioDataCache) {
        if (audioBufferCache.has(char)) continue;

        const promise = audioManager.audioContext.decodeAudioData(arrayBuffer)
            .then(audioBuffer => {
                audioBufferCache.set(char, audioBuffer);
                audioDataCache.delete(char);
                if (DOM) {
                    UI.updateLoadingProgress(DOM, audioBufferCache.size, ALL_CHARACTERS.length);
                }
            })
            .catch(err => {
                audioDataCache.delete(char);
                console.error(`Failed to decode audio for ${char}:`, err);
            });

        decodePromises.push(promise);
    }

    await Promise.all(decodePromises);
}

/**
 * Play character audio
 */
function playCharacterAudio(char, callback) {
    const buffer = audioBufferCache.get(char);

    if (buffer && audioManager.audioContext) {
        try {
            const source = audioManager.audioContext.createBufferSource();
            source.buffer = buffer;
            
            // Create gain node for voice volume control
            const gainNode = audioManager.audioContext.createGain();
            gainNode.gain.value = currentVoiceVolumePercent / 100; // Convert percentage to 0-1
            
            // Connect: source -> gain -> master
            source.connect(gainNode);
            gainNode.connect(audioManager.masterGainNode);
            source.start(0);

            // Track the source so stopAll() silences the voice reveal too
            audioManager.activeNodes.add(source);

            source.onended = () => {
                audioManager.activeNodes.delete(source);
                try {
                    source.disconnect();
                    gainNode.disconnect();
                } catch (e) {
                    // Already disconnected
                }
                if (callback) callback();
            };
        } catch (err) {
            console.error('Error playing audio buffer:', err);
            if (callback) callback();
        }
    } else {
        if (callback) callback();
    }
}

/**
 * Get recognition time from slider in milliseconds
 */
function getRecognitionTimeMs() {
    const value = parseInt(DOM.pauseDurationSlider?.value, 10);
    return isNaN(value) ? 500 : value;
}

/**
 * Get review time from slider in milliseconds
 */
function getReviewTimeMs() {
    const value = parseInt(DOM.reviewDurationSlider?.value, 10);
    return isNaN(value) ? 1000 : value;
}

/**
 * Get current performance data with consistent accuracy calculation
 * Uses robust accuracy calculation with multiple techniques
 * This is the single source of truth for all performance UI elements
 * @returns {{accuracy: number, zone: string, zoneDetails: Object, recognitionTime: number, adjustments: Object, robustData: Object}}
 */
function getCurrentPerformanceData() {
    // Calculate robust accuracy data
    const robustData = calculateRobustAccuracy(recentAttempts, attemptsSinceRecognitionChange);
    
    // Default to 80% (center of training zone) when no attempts yet
    const displayAccuracy = robustData.sampleSize === 0 ? 80 : robustData.accuracy;
    
    const zoneDetails = getPerformanceZone(displayAccuracy);
    const recognitionTimeMs = getRecognitionTimeMs();
    const adjustments = calculateRecognitionAdjustments(recognitionTimeMs);
    
    return {
        accuracy: displayAccuracy,
        zone: zoneDetails.zone,  // 'too-hard', 'learning', or 'too-easy'
        zoneDetails,
        recognitionTime: recognitionTimeMs,
        adjustments,
        robustData  // Include full robust data for recommendations
    };
}

/**
 * Request wake lock
 */
async function requestWakeLock() {
    if ('wakeLock' in navigator) {
        try {
            wakeLock = await navigator.wakeLock.request('screen');

            wakeLock.addEventListener('release', () => {
                wakeLock = null;
            });
        } catch (err) {
            console.error('Wake lock request failed:', err);
        }
    }
}

/**
 * Release wake lock
 */
async function releaseWakeLock() {
    if (wakeLock) {
        try {
            await wakeLock.release();
            wakeLock = null;
        } catch (err) {
            console.error('Wake lock release failed:', err);
        }
    }
}

/**
 * Start practice session
 */
async function startPractice() {
    // Synchronous guard: a second click while audio is still initializing
    // must not start a second concurrent practice chain
    if (isPlaying || isStarting) return;
    isStarting = true;

    // Scroll to top of page when starting practice
    window.scrollTo({ top: 0, behavior: 'smooth' });

    UI.hideSessionSummary(DOM);

    try {
        // Show decode progress if the voice buffers aren't ready yet
        if (audioBufferCache.size < ALL_CHARACTERS.length) {
            UI.updateLoadingProgress(DOM, audioBufferCache.size, ALL_CHARACTERS.length);
            UI.showLoadingOverlay(DOM);
        }
        await ensureAudio();
    } finally {
        isStarting = false;
        UI.hideLoadingOverlay(DOM);
    }

    isPlaying = true;
    sessionBestStreak = 0;
    sessionMissCounts = {};
    sessionStats = {
        correct: 0,
        incorrect: 0,
        startTime: Date.now(),
        elapsedTimeMs: 0
    };

    // Update UI
    UI.updatePracticeState(DOM, true);
    
    // Disable voice selector during practice
    if (DOM.voiceSelect) {
        DOM.voiceSelect.disabled = true;
    }
    if (DOM.voiceHelperText) {
        DOM.voiceHelperText.style.display = 'block';
        // Deferred so the opacity transition runs
        schedule(() => {
            DOM.voiceHelperText.classList.add('show');
        }, 10);
    }
    
    // Reset timer display to 0:00
    UI.updateTimerDisplay(DOM, '0:00');

    updateCharStatsDisplay();
    UI.updateOopsButton(DOM, currentPhase, currentChar, previousChar, markedCharacter);

    // Request wake lock
    requestWakeLock();

    // Start timer
    startTimer();

    // Generate practice queue
    practiceQueue = generatePracticeQueue(charStats, ALL_CHARACTERS, 10, previousChar || null);

    // Start practice
    if (practiceQueue.length > 0) {
        playNextCharacter();
    }
}

/**
 * Stop practice session
 */
function stopPractice() {
    const wasPlaying = isPlaying;
    isPlaying = false;

    // Show the session summary when a session with recorded attempts ends
    if (wasPlaying) {
        if (sessionStats.startTime) {
            sessionStats.elapsedTimeMs = Date.now() - sessionStats.startTime;
        }
        const sessionTotals = calculateSessionStats(sessionStats);
        if (sessionTotals.total > 0) {
            const toughest = Object.entries(sessionMissCounts)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 3)
                .map(([char]) => char);

            UI.showSessionSummary(DOM, {
                total: sessionTotals.total,
                accuracy: sessionTotals.accuracy,
                durationText: formatTime(Math.floor(sessionStats.elapsedTimeMs / 1000)),
                bestStreak: sessionBestStreak,
                toughest
            });
        }
    }

    currentChar = '';
    previousChar = '';
    previousCharSnapshot = null;
    currentPhase = 'idle';
    oopsPressed = false;
    previousRecordedAsSuccess = false;
    oopsCooldown = false;
    markedCharacter = null;

    // Cancel every pending timer and flush any pending progress save
    clearAllTimers();
    clearInterval(elapsedTimeInterval);
    saveProgress();

    // Stop audio and the waveform animation
    stopSound();
    UI.stopWaveform();

    // Release wake lock
    releaseWakeLock();

    // Update UI
    UI.updatePracticeState(DOM, false);

    // Re-enable voice selector
    if (DOM.voiceSelect) {
        DOM.voiceSelect.disabled = false;
    }
    if (DOM.voiceHelperText) {
        DOM.voiceHelperText.classList.remove('show');
        // Hide after transition completes
        schedule(() => {
            DOM.voiceHelperText.style.display = 'none';
        }, 200);
    }
}

/**
 * Handle oops button press
 */
function handleOops(event) {
    if (!isPlaying || oopsCooldown) return;

    if (DOM.oopsButton && DOM.oopsButton.classList.contains('oops-marked')) return;

    // Prevent double-tap
    oopsCooldown = true;
    schedule(() => {
        oopsCooldown = false;
    }, 200);

    // Determine which character to mark
    let charToMark = null;
    if (currentPhase === 'reveal' || currentPhase === 'oops-window') {
        charToMark = currentChar;
        oopsPressed = true;
    } else if (currentPhase === 'morse' || currentPhase === 'recognition') {
        charToMark = previousChar;
        if (charToMark && previousRecordedAsSuccess) {
            // Undo the previous success: re-score it as incorrect from the
            // pre-update SM-2 snapshot so repetitions/ease/interval are exact
            charStats = revertToIncorrect(charStats, charToMark, previousCharSnapshot);
            previousRecordedAsSuccess = false;
            sessionStats.correct--;
            sessionStats.incorrect++;
            currentStreak = 0;
            sessionMissCounts[charToMark] = (sessionMissCounts[charToMark] || 0) + 1;

            // Update the recent attempts to reflect the correction
            for (let i = recentAttempts.length - 1; i >= 0; i--) {
                if (recentAttempts[i].char === previousChar) {
                    recentAttempts[i].correct = false;
                    break;
                }
            }

            updateSessionStatsDisplay();
            updateCharStatsDisplay();
            scheduleProgressSave();
        }
    }

    if (charToMark) {
        markedCharacter = charToMark;
    }

    // Visual feedback
    UI.markOopsButton(DOM);
    if (event) {
        UI.createRipple(DOM.oopsButton, event);
    }
}

/**
 * Play next character
 */
function playNextCharacter() {
    if (!isPlaying) return;

    // Generate new queue if empty
    if (practiceQueue.length === 0) {
        practiceQueue = generatePracticeQueue(charStats, ALL_CHARACTERS, 10, previousChar || null);
        if (practiceQueue.length === 0) {
            stopPractice();
            return;
        }
    }

    // Get next character
    const nextChar = practiceQueue[0];
    practiceQueue = practiceQueue.slice(1);

    playCharacter(nextChar);
}

/**
 * Play a single character
 */
function playCharacter(char) {
    currentChar = char;
    oopsPressed = false;

    // Cancel previous phase timer
    cancelScheduled(practiceTimer);
    practiceTimer = null;

    // Reset display
    UI.hideCharacter(DOM);
    UI.showListeningIndicator(DOM);

    const morseCode = MORSE_CODE[char];

    // Update phase
    currentPhase = 'morse';
    UI.updateOopsButton(DOM, currentPhase, currentChar, previousChar, markedCharacter);

    // Animate waveform
    const timings = {
        dotDurationMs: calculateDotDurationMs(currentSpeedWpm),
        dashDurationMs: calculateDashDurationMs(currentSpeedWpm),
        symbolSpacingMs: calculateSymbolSpacingMs(currentSpeedWpm)
    };
    UI.animateWaveform(morseCode, timings);

    // Play morse code
    schedule(() => {
        if (!isPlaying) return;

        try {
            playMorseSequence(morseCode, currentSpeedWpm, currentToneFreqHz, () => {
                if (!isPlaying) return;

                // Enter recognition phase
                currentPhase = 'recognition';
                UI.updateOopsButton(DOM, currentPhase, currentChar, previousChar, markedCharacter);

                const recognitionTimeMs = getRecognitionTimeMs();

                practiceTimer = schedule(() => {
                    if (!isPlaying) return;

                    // Reveal phase
                    currentPhase = 'reveal';
                    UI.updateOopsButton(DOM, currentPhase, currentChar, previousChar, markedCharacter);

                    // Show character
                    UI.showCharacter(DOM, char);

                    // Play character audio
                    playCharacterAudio(char, () => {
                        if (!isPlaying) return;

                        currentPhase = 'oops-window';
                        const reviewTimeMs = getReviewTimeMs();

                        practiceTimer = schedule(() => {
                            recordResult();
                            playNextCharacter();
                        }, reviewTimeMs);
                    });
                }, recognitionTimeMs);
            });
        } catch (err) {
            // Keep the session advancing instead of freezing silently
            console.error('Failed to play morse sequence:', err);
            practiceTimer = schedule(playNextCharacter, 500);
        }
    }, 50);
}

/**
 * Record result
 */
function recordResult() {
    if (!isPlaying || !currentChar) return;

    const correct = !oopsPressed;

    // Update session stats
    sessionStats.correct += correct ? 1 : 0;
    sessionStats.incorrect += correct ? 0 : 1;

    // Track attempts since recognition change
    attemptsSinceRecognitionChange++;

    // Update streak
    if (correct) {
        currentStreak++;
        sessionBestStreak = Math.max(sessionBestStreak, currentStreak);
    } else {
        currentStreak = 0;
        sessionMissCounts[currentChar] = (sessionMissCounts[currentChar] || 0) + 1;

        // Track confusion
        const candidates = analyzeConfusion(currentChar, PATTERN_GROUPS);
        if (candidates.length > 0) {
            const mostLikely = candidates[0].char;
            confusionMatrix = updateConfusionMatrix(confusionMatrix, currentChar, mostLikely);
        }
    }

    // Visible confirmation of the recorded result
    const clearFlash = UI.flashResult(DOM, correct);
    if (clearFlash) {
        schedule(clearFlash, 500);
    }

    // Track attempt
    recentAttempts = trackAttempt(recentAttempts, currentChar, correct, RECENT_ATTEMPTS_COUNT);

    // Update character stats, keeping a snapshot so a late Oops can revert exactly
    previousCharSnapshot = { ...charStats[currentChar] };
    charStats = updateCharacterStats(charStats, currentChar, correct);


    // Update displays
    updateSessionStatsDisplay();
    updateCharStatsDisplay();

    // Update state
    previousChar = currentChar;
    previousRecordedAsSuccess = correct;
    currentChar = '';

    // Persist progress (debounced)
    scheduleProgressSave();
}

/**
 * Start timer
 */
function startTimer() {
    clearInterval(elapsedTimeInterval);
    elapsedTimeInterval = setInterval(() => {
        sessionStats.elapsedTimeMs = Date.now() - sessionStats.startTime;
        UI.updateTimerDisplay(DOM, formatTime(Math.floor(sessionStats.elapsedTimeMs / 1000)));
    }, TIMER_UPDATE_INTERVAL_MS);
}

/**
 * Update session stats display
 */
function updateSessionStatsDisplay() {
    UI.updateTimerDisplay(DOM, formatTime(Math.floor(sessionStats.elapsedTimeMs / 1000)));
}

/**
 * Update character stats display
 */
function updateCharStatsDisplay() {
    // Update training zone gauge
    updateTrainingZoneGauge();

    // Update focus queue
    const priorityScores = getCharacterPriorityScores(charStats, confusionMatrix);
    UI.updateFocusQueue(DOM, priorityScores, confusionMatrix);

    // Update recently practiced stream with results
    UI.updatePracticeStream(DOM, recentAttempts, charStats);
}

/**
 * Update training zone gauge
 */
function updateTrainingZoneGauge() {
    if (!isPlaying) return;
    
    const perfData = getCurrentPerformanceData();
    const recommendation = getSpeedRecommendationRobust(
        perfData.robustData, 
        perfData.recognitionTime, 
        sessionStats.correct + sessionStats.incorrect, 
        attemptsSinceRecognitionChange
    );
    
    UI.updateTrainingZoneGauge(
        DOM,
        perfData.accuracy,
        perfData.zone,
        perfData.recognitionTime,
        perfData.adjustments,
        recommendation,
        perfData.robustData.trend,
        sessionStats.correct + sessionStats.incorrect,
        attemptsSinceRecognitionChange
    );
}

/**
 * Update platform-specific tips
 */
function updatePlatformTips() {
    const tipElement = document.querySelector('.quick-tips .tip');
    const learnStepText = document.querySelector('.learn-step-text');
    const keyboardShortcuts = document.getElementById('keyboard-shortcuts');
    
    if (hasTouchCapability()) {
        // Touch device updates
        if (tipElement) {
            tipElement.innerHTML = 'Tap Start to begin';
        }
        if (learnStepText) {
            learnStepText.textContent = 'Mark mistakes';
        }
        // Hide keyboard shortcuts in settings
        if (keyboardShortcuts) {
            keyboardShortcuts.style.display = 'none';
        }
    } else {
        // Non-touch device updates
        if (tipElement) {
            tipElement.innerHTML = 'Press <kbd>S</kbd> to start • <kbd>Space</kbd> to mark mistakes';
        }
        if (learnStepText) {
            learnStepText.textContent = 'Mark mistakes with Space';
        }
        // Show keyboard shortcuts in settings
        if (keyboardShortcuts) {
            keyboardShortcuts.style.display = 'block';
        }
    }
}

/**
 * Handle PWA shortcut
 */
function handlePWAShortcut() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('action') === 'start') {
        window.history.replaceState({}, document.title, window.location.pathname);
        schedule(() => {
            startPractice();
        }, 1000);
    }
}

/**
 * Cleanup function
 */
function cleanup() {
    // Flush any pending progress save
    saveProgress();

    // Clean up event listeners
    eventListeners.forEach((handlers) => {
        handlers.forEach(({ element, event, handler, options }) => {
            element.removeEventListener(event, handler, options);
        });
    });
    eventListeners.clear();

    // Stop audio
    stopSound();

    // Release wake lock
    releaseWakeLock();

    // Clear timers
    clearAllTimers();
    clearInterval(elapsedTimeInterval);

    // Close audio context
    if (audioManager.audioContext && audioManager.audioContext.state !== 'closed') {
        audioManager.audioContext.close();
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
} else {
    initialize();
}